import { G } from '@mobily/ts-belt';
import { assign, createMachine } from 'xstate';
import { recordPageReady } from '@stitch-fix/client-analytics-reporter';
import { SearchFragment } from '../../hooks/getSearchResults';
import { trackEvent, SearchPageView, SEARCH_RESULTS_EVENT_CATEGORY } from '../../events';
interface ResultContext {
  data?: SearchFragment;
  error?: string;
}
type ResultsEvent = {
  type: 'FETCH';
} | {
  type: 'UPDATE';
} | {
  type: 'RESOLVE';
  data?: SearchFragment;
} | {
  type: 'REJECT';
  error: string;
};
type ResultsTypestate = {
  value: 'empty';
  context: ResultContext & {
    data: SearchFragment;
    error: undefined;
  };
} | {
  value: 'loading';
  context: ResultContext & {
    data: undefined;
    error: undefined;
  };
} | {
  value: 'success' | {
    success: 'idle' | 'updating';
  };
  context: ResultContext & {
    data: SearchFragment;
    error: undefined;
  };
} | {
  value: 'failure';
  context: ResultContext & {
    data: undefined;
    error: string;
  };
};

// Since performing a new search from the search page does not cause a page reload,
// we need to only call `recordPageReady` when a client initially navigates to the search page.
let hasPageLoaded = false;

/**
 * The results machine handles states regarding initial and subsequent requests
 * for search results. A simple diagram of possible states looks like the
 * following.
 *
 * - loading
 * - success
 *   - idle
 *   - updating (clicking `SaveBookmark` button)
 * - failure
 * - empty (no results)
 */
export const resultsMachine = createMachine<ResultContext, ResultsEvent, ResultsTypestate>({
  id: 'results',
  initial: 'loading',
  states: {
    loading: {
      on: {
        RESOLVE: [{
          target: 'success',
          cond: 'hasResults'
        }, {
          target: 'empty'
        }],
        REJECT: {
          target: 'failure'
        }
      }
    },
    success: {
      initial: 'idle',
      entry: ['sendSuccessEvent', 'recordPageReady'],
      states: {
        idle: {
          entry: ['handleEventAssign'],
          on: {
            RESOLVE: {
              actions: 'handleEventAssign'
            },
            UPDATE: {
              target: 'updating'
            }
          }
        },
        updating: {
          on: {
            RESOLVE: {
              target: 'idle'
            }
          }
        }
      }
    },
    failure: {
      entry: ['handleEventAssign']
    },
    empty: {
      entry: ['handleEventAssign']
    }
  }
}, {
  actions: {
    handleEventAssign: assign((_, e) => {
      switch (e.type) {
        case 'REJECT':
          return {
            error: e.error
          };
        case 'RESOLVE':
          return {
            data: e.data
          };
        default:
          return {};
      }
    }),
    sendSuccessEvent: (_, e) => {
      if (e.type === 'RESOLVE' && G.isObject(e.data) && G.isString(e.data.queryId)) {
        const event: SearchPageView = {
          action_name: 'page_view',
          action_type: 'view',
          canonical_name: SEARCH_RESULTS_EVENT_CATEGORY,
          event_name: SEARCH_RESULTS_EVENT_CATEGORY,
          additional_context: {
            direct_buy: {
              query_id: e.data.queryId
            }
          }
        };
        trackEvent(event);
      }
    },
    recordPageReady: (_, e) => {
      if (!hasPageLoaded && e.type === 'RESOLVE') {
        hasPageLoaded = true;
        const imagesAboveTheFold = e.data?.shopProductsByPage.edges.slice(0, 4).map(({
          node
        }) => node.variantBySkuId?.media[0].mediaPath).filter(G.isString);
        recordPageReady({
          resources: imagesAboveTheFold
        });
      }
    }
  },
  guards: {
    hasResults: (_, e) => Boolean(e.type === 'RESOLVE' && e.data && e.data.shopProductsByPage.edges.length > 0)
  }
});